import React from 'react';
import { Form, Select } from 'antd';
import { ErrorHandler, LoadingState } from '@common/components';
import { selectInputFilterHelper } from '@common/services';
import { gql, useQuery } from '@common/hooks';
import { OrganizationUnitScope } from '@institutions/graphql';
import { SizeType } from 'antd/lib/config-provider/SizeContext';

const OrganizationUnitsSelectComponentQuery = gql`
  query OrganizationUnitsSelectComponentQuery(
    $input: OrganizationUnitsInput
    $fetchOrgBusiness: Boolean!
  ) {
    organizationUnits(input: $input) {
      id
      name
      organizationBusiness @include(if: $fetchOrgBusiness) {
        id
      }
      department {
        id
      }
    }
  }
`;

type OrganizationUnitsResponse = {
  organizationUnits: OrganizationUnit[];
};

type OrganizationUnitsVariables = {
  input?: {
    scope?: OrganizationUnitScope;
  };
  fetchOrgBusiness?: boolean;
};

type OrganizationUnit = {
  id: string;
  name: string;
  organizationBusiness?: OrganizationBusiness;
};

type OrganizationBusiness = {
  id: string;
};

export type Props = {
  organizationBusinessId?: string;
  disabled?: boolean;
  size?: SizeType;
  onChange?: (unit: OrganizationUnit) => void;
  scope?: OrganizationUnitScope;
};

export default function OrganizationUnitSelect({
  organizationBusinessId = '',
  disabled = false,
  size = 'large',
  onChange,
  scope = 'default',
  ...props
}: Props) {
  const { loading, error, data } = useQuery<
    OrganizationUnitsResponse,
    OrganizationUnitsVariables
  >(OrganizationUnitsSelectComponentQuery, {
    variables: {
      input: {
        scope: scope,
      },
      fetchOrgBusiness: organizationBusinessId !== '',
    },
  });

  if (loading || !data) {
    return <LoadingState />;
  }

  if (error) {
    return (
      <ErrorHandler
        error={error}
        log="OrganizationUnitSelect"
        messageType="contextual"
      />
    );
  }

  return (
    <Form.Item
      label="Unit"
      name="organizationUnitId"
      colon={false}
      rules={[
        {
          required: true,
          message: 'Please select an organization unit',
        },
      ]}
      {...props}
    >
      <Select
        disabled={disabled}
        showSearch
        filterOption={selectInputFilterHelper}
        size={size}
        placeholder="Select unit"
        getPopupContainer={(trigger) => trigger.parentNode}
        onChange={(value) => {
          const selectedUnit = data.organizationUnits.find(
            (unit) => unit.id === value
          );
          if (onChange && selectedUnit) onChange(selectedUnit);
        }}
      >
        {data.organizationUnits
          .filter(({ organizationBusiness }) => {
            if (organizationBusinessId) {
              return organizationBusiness?.id === organizationBusinessId;
            }

            return true;
          })
          .map(({ id, name }) => {
            return (
              <Select.Option key={id} value={id}>
                {name}
              </Select.Option>
            );
          })}
      </Select>
    </Form.Item>
  );
}
